import { memo, useCallback, useMemo, useState } from "react";
import { geographyOptions, metricCatalogue } from "@/features/explorer/data/catalogue";
import { COMPOSITE_PRESETS, METRIC_DIRECTIONS, isMetricId } from "@/features/explorer/lib/composite-index";
import { filterMetricsByQuery } from "@/features/explorer/lib/metric-search";
import { resolveSelectionUpdate } from "@/features/explorer/lib/selection-update";
import { useAutoMinimize } from "@/hooks/use-auto-minimize";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import type {
  CompositePreset,
  CompositeWeightMap,
  ExplorerSelection,
  MetricDefinition,
  MetricId,
} from "@/features/explorer/types";
import { Button } from "@/components/ui/button";
import { CollapsibleRegion } from "@/components/ui/collapsible-region";
import { Input } from "@/components/ui/input";
import {
  SlidersHorizontal,
  ChevronDown,
  Layers,
  MapPin,
  Banknote,
  Home,
  Percent,
  Users,
  TrendingUp,
  Wallet,
  Award,
  Activity,
  Leaf,
  Zap,
  GraduationCap,
  HeartPulse,
  Sparkles,
  Check,
  RotateCcw,
  Search,
  X,
  BarChart3,
  Factory,
  Briefcase,
  Flame,
  Baby,
  Building2,
  Wifi,
  Signal,
  Dumbbell,
  Smile,
  KeyRound,
  Timer,
  School,
  Brain,
  Ruler,
} from "lucide-react";
import { cn } from "@/lib/utils";

type ExplorerControlsProps = {
  selection: ExplorerSelection;
  onSelectionChange: (selection: ExplorerSelection) => void;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  activeTab?: "metrics" | "geography" | "index";
  onActiveTabChange?: (tab: "metrics" | "geography" | "index") => void;
  disableAutoMinimize?: boolean;
  compositeWeights: CompositeWeightMap;
  onCompositeWeightsChange: (weights: CompositeWeightMap) => void;
  onResetComposite: () => void;
  /**
   * Surface treatment. `floating` (default) renders the glass panel with its
   * own header, used on desktop; `sheet` renders borderless/blur-free with no
   * header for embedding in the mobile bottom sheet, which already provides an
   * opaque surface plus title and close button.
   */
  variant?: "floating" | "sheet";
};

const METRIC_ICONS: Record<MetricId, React.ElementType> = {
  "average-house-price": Home,
  "median-annual-pay": Banknote,
  "mean-annual-pay": TrendingUp,
  "weekly-pay": Banknote,
  "housing-affordability-ratio": Percent,
  "employment-rate": Percent,
  population: Users,
  "gdhi-per-head": Wallet,
  "gva-per-head": Award,
  "claimant-count-rate": Activity,
  "co2-emissions-per-capita": Leaf,
  "ev-charger-density": Zap,
  "nvq4-qualifications-rate": GraduationCap,
  "life-expectancy": HeartPulse,
  "gdp-per-head": BarChart3,
  "business-demography": Factory,
  "modelled-unemployment": Briefcase,
  "fuel-poverty": Flame,
  "children-in-relative-poverty": Baby,
  "net-additions-to-the-housing-stock": Building2,
  "gigabit-capable-broadband": Wifi,
  "network-coverage-5g": Signal,
  "adult-obesity-prevalence": Dumbbell,
  "personal-wellbeing": Smile,
  "first-time-buyer-mortgage-sales": KeyRound,
  "car-commute-time": Timer,
  "gcse-attainment": School,
  "pupils-at-expected-standard": School,
  "apprenticeship-starts": GraduationCap,
  "fe-learner-achievements": GraduationCap,
  "fe-participation": GraduationCap,
  "no-qualifications": Brain,
  "persistent-absences-all": School,
  "persistent-absences-fsm": School,
  "child-obesity": Dumbbell,
  "adult-smokers": Flame,
  "early-years-language": Brain,
  "cardiovascular-mortality": HeartPulse,
  "electricity-consumption": Zap,
  "gas-consumption": Zap,
  "coverage-4g": Signal,
  "premises-below-30mbps": Signal,
  "population-density": Ruler,
};

type MetricButtonProps = {
  item: (typeof metricCatalogue)[number];
  isSelected: boolean;
  onSelect: () => void;
  icon: React.ElementType;
};

const MetricButton = memo(({ item, isSelected, onSelect, icon: IconComponent }: MetricButtonProps) => {
  return (
    <button
      type='button'
      onClick={onSelect}
      className={cn(
        "flex w-full cursor-pointer items-start gap-3 rounded-md border p-3 text-left transition-all",
        isSelected
          ? "border-primary/60 bg-primary/10 text-card-foreground shadow-sm font-bold"
          : "border-border bg-muted/40 text-muted-foreground hover:border-primary/40 hover:bg-muted/80 hover:text-card-foreground",
      )}>
      <IconComponent className={cn("mt-1 size-4 shrink-0", isSelected ? "text-primary" : "text-muted-foreground")} />
      <div className='min-w-0 flex-1 space-y-1'>
        <div className='flex items-center justify-between gap-2'>
          <span className='text-base font-bold text-card-foreground leading-snug'>{item.label}</span>
          {isSelected && (
            <div className='flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground'>
              <Check className='size-3' />
            </div>
          )}
        </div>
        <p className='text-sm leading-normal text-muted-foreground font-normal'>{item.description}</p>
      </div>
    </button>
  );
});

MetricButton.displayName = "MetricButton";

type GeographyButtonProps = {
  option: (typeof geographyOptions)[number];
  isSelected: boolean;
  onSelect: () => void;
};

const GeographyButton = memo(({ option, isSelected, onSelect }: GeographyButtonProps) => {
  return (
    <button
      type='button'
      onClick={onSelect}
      className={cn(
        "flex w-full cursor-pointer items-center justify-between rounded-md border p-3 text-left transition-all",
        isSelected
          ? "border-primary/60 bg-primary/10 text-card-foreground shadow-sm font-bold"
          : "border-border bg-muted/40 text-muted-foreground hover:border-primary/40 hover:bg-muted/80 hover:text-card-foreground",
      )}>
      <div className='flex items-center gap-2.5'>
        <MapPin className={cn("size-4", isSelected ? "text-primary" : "text-muted-foreground")} />
        <span className='text-base font-semibold text-card-foreground'>{option.label}</span>
      </div>
      {isSelected && (
        <div className='flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground'>
          <Check className='size-3' />
        </div>
      )}
    </button>
  );
});

GeographyButton.displayName = "GeographyButton";

export const ExplorerControls = memo(
  ({
    selection,
    onSelectionChange,
    isExpanded = true,
    onToggleExpand,
    activeTab: controlledActiveTab,
    onActiveTabChange,
    disableAutoMinimize = false,
    compositeWeights,
    onCompositeWeightsChange,
    onResetComposite,
    variant = "floating",
  }: ExplorerControlsProps) => {
    const [internalTab, setInternalTab] = useState<"metrics" | "geography" | "index">("metrics");
    const activeTab = controlledActiveTab ?? internalTab;
    const setActiveTab = useCallback(
      (nextTab: "metrics" | "geography" | "index") => {
        setInternalTab(nextTab);
        onActiveTabChange?.(nextTab);
      },
      [onActiveTabChange],
    );
    const [searchQuery, setSearchQuery] = useState("");

    const debouncedSearchQuery = useDebouncedValue(searchQuery, 250);
    const filteredMetrics = useMemo(
      () => filterMetricsByQuery(debouncedSearchQuery, metricCatalogue),
      [debouncedSearchQuery],
    );

    const activeMetricEntries = useMemo(() => {
      const entries: [MetricId, number][] = [];
      for (const [id, weight] of Object.entries(compositeWeights)) {
        if (typeof weight === "number" && weight > 0 && isMetricId(id)) entries.push([id, weight]);
      }
      return entries;
    }, [compositeWeights]);
    const isCompositeActive = activeMetricEntries.length > 0;
    const totalWeight = useMemo(
      () => Object.values(compositeWeights).reduce((sum: number, w) => sum + (w || 0), 0),
      [compositeWeights],
    );

    const updateSelection = useCallback(
      (next: Partial<ExplorerSelection>) => {
        // Selecting a standard dataset metric deactivates the custom composite index
        if (isCompositeActive) {
          onCompositeWeightsChange({});
        }
        onSelectionChange(resolveSelectionUpdate(selection, next));
      },
      [isCompositeActive, onCompositeWeightsChange, onSelectionChange, selection],
    );

    const handleMetricSelectHandlers = useMemo(() => {
      const handlers: Record<string, () => void> = {};
      for (const item of metricCatalogue) {
        handlers[item.id] = () => {
          updateSelection({ metricId: item.id, statisticId: item.supportedStatistics[0] });
        };
      }
      return handlers;
    }, [updateSelection]);

    const handleGeographySelectHandlers = useMemo(() => {
      const handlers: Record<string, () => void> = {};
      for (const option of geographyOptions) {
        handlers[option.value] = () => {
          updateSelection({ geographyLevel: option.value });
        };
      }
      return handlers;
    }, [updateSelection]);

    const handleSliderChange = useCallback(
      (metricId: MetricId, newWeight: number) => {
        const nextWeights = { ...compositeWeights };
        if (newWeight > 0) {
          nextWeights[metricId] = newWeight;
        } else {
          delete nextWeights[metricId];
        }
        onCompositeWeightsChange(nextWeights);
      },
      [compositeWeights, onCompositeWeightsChange],
    );

    const handleApplyPreset = useCallback(
      (preset: CompositePreset) => {
        onCompositeWeightsChange(preset.weights);
      },
      [onCompositeWeightsChange],
    );

    const containerRef = useAutoMinimize({
      isExpanded,
      disabled: disableAutoMinimize,
      onMinimize: () => {
        if (isExpanded && onToggleExpand) {
          onToggleExpand();
        }
      },
      delayMs: 5000,
    });

    return (
      <div
        ref={containerRef}
        className={cn(
          "flex w-full flex-col font-sans",
          variant === "sheet"
            ? "bg-transparent text-card-foreground"
            : "max-h-[calc(100vh-6rem)] lg:max-h-[calc(100vh-7rem)] overflow-hidden rounded-lg border border-border bg-card/95 text-card-foreground backdrop-blur-2xl transition-shadow duration-300 animate-in fade-in-0",
          variant === "floating" && (isExpanded ? "shadow-2xl" : "shadow-lg"),
        )}>
        {/* Clickable Header Bar (desktop only; the mobile sheet provides its own title + close) */}
        {variant === "floating" && (
          <button
            type='button'
            onClick={onToggleExpand}
            aria-expanded={isExpanded}
            aria-controls='map-controls-body'
            aria-label={isExpanded ? "Minimize map controls" : "Expand map controls"}
            className={cn(
              "shrink-0 flex w-full cursor-pointer items-center justify-between px-4 py-3 text-left transition-colors hover:bg-muted/40",
              isExpanded && "border-b border-border",
            )}>
            <div className='flex items-center gap-2'>
              <SlidersHorizontal className='size-4 text-primary' />
              <h2 className='text-base font-bold tracking-wider text-card-foreground uppercase'>
                {isExpanded ? "Map Controls" : "Map Filters & Controls"}
              </h2>
            </div>
            <ChevronDown
              className={cn(
                "size-4 text-muted-foreground transition-transform duration-300",
                isExpanded && "rotate-180",
              )}
            />
          </button>
        )}

        <CollapsibleRegion open={isExpanded} id='map-controls-body'>
          <div className={variant === "sheet" ? "" : "px-4 pb-4 pt-3"}>
            {/* Justified 3-Way Segmented Controller */}
            <div className='shrink-0 mb-3 grid grid-cols-3 gap-1 rounded-md border border-border bg-muted/60 p-1 text-xs font-semibold'>
              <button
                type='button'
                onClick={() => {
                  setActiveTab("metrics");
                }}
                className={cn(
                  "flex cursor-pointer items-center justify-center gap-1 sm:gap-1.5 rounded-sm py-2 px-1 text-xs font-bold transition-all min-w-0",
                  activeTab === "metrics"
                    ? "bg-primary text-primary-foreground shadow-xs"
                    : "text-muted-foreground hover:bg-muted/80 hover:text-card-foreground",
                )}>
                <Layers className='size-3.5 shrink-0' />
                <span className='truncate'>Metrics</span>
              </button>

              <button
                type='button'
                onClick={() => {
                  setActiveTab("geography");
                }}
                className={cn(
                  "flex cursor-pointer items-center justify-center gap-1 sm:gap-1.5 rounded-sm py-2 px-1 text-xs font-bold transition-all min-w-0",
                  activeTab === "geography"
                    ? "bg-primary text-primary-foreground shadow-xs"
                    : "text-muted-foreground hover:bg-muted/80 hover:text-card-foreground",
                )}>
                <MapPin className='size-3.5 shrink-0' />
                <span className='truncate'>Boundary</span>
              </button>

              <button
                type='button'
                id='tour-composite-tab'
                onClick={() => {
                  setActiveTab("index");
                }}
                className={cn(
                  "relative flex cursor-pointer items-center justify-center gap-1 sm:gap-1.5 rounded-sm py-2 px-1 text-xs font-bold transition-all min-w-0",
                  activeTab === "index"
                    ? "bg-primary text-primary-foreground shadow-xs"
                    : isCompositeActive
                      ? "border border-primary/50 bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-muted/80 hover:text-card-foreground",
                )}>
                <Sparkles
                  className={cn(
                    "size-3.5 shrink-0",
                    activeTab === "index"
                      ? "text-primary-foreground"
                      : isCompositeActive
                        ? "text-primary"
                        : "text-muted-foreground",
                  )}
                />
                <span className='truncate'>Index</span>
                {isCompositeActive && activeTab !== "index" && (
                  <span className='size-1.5 rounded-full bg-primary animate-pulse shrink-0' />
                )}
              </button>
            </div>

            {/* Tab 1: Dataset Metric */}
            {activeTab === "metrics" && (
              <div className='flex flex-col min-h-0 flex-1 gap-2.5'>
                <div className='shrink-0 relative'>
                  <Search
                    className='absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground pointer-events-none'
                    aria-hidden='true'
                  />
                  <Input
                    type='search'
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                    }}
                    placeholder='Search metrics...'
                    aria-label='Search metrics'
                    autoComplete='off'
                    spellCheck={false}
                    className='h-9 rounded-md px-9 text-base md:text-sm [&::-webkit-search-cancel-button]:hidden'
                  />
                  {searchQuery && (
                    <Button
                      type='button'
                      variant='ghost'
                      size='icon'
                      onClick={() => {
                        setSearchQuery("");
                      }}
                      aria-label='Clear metric search'
                      className='absolute top-1/2 right-1 size-7 -translate-y-1/2 rounded-md text-muted-foreground hover:text-card-foreground'>
                      <X className='size-3.5' />
                    </Button>
                  )}
                </div>

                <div
                  className={
                    variant === "sheet"
                      ? "space-y-2.5 text-base"
                      : "max-h-[calc(100vh-16rem)] lg:max-h-[calc(100vh-18rem)] space-y-2.5 overflow-y-auto pr-1.5 text-base"
                  }>
                  {filteredMetrics.map((item) => {
                    const IconComponent = METRIC_ICONS[item.id] || Layers;
                    const isSelected = !isCompositeActive && selection.metricId === item.id;

                    return (
                      <MetricButton
                        key={item.id}
                        item={item}
                        isSelected={isSelected}
                        icon={IconComponent}
                        onSelect={handleMetricSelectHandlers[item.id]}
                      />
                    );
                  })}

                  {filteredMetrics.length === 0 && (
                    <div className='rounded-md border border-dashed border-border bg-muted/30 p-4 text-center text-sm text-muted-foreground'>
                      No metrics match{" "}
                      <span className='font-semibold text-card-foreground'>&ldquo;{searchQuery}&rdquo;</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tab 2: Geography Granularity Buttons */}
            {activeTab === "geography" && (
              <div className='space-y-2.5 text-base animate-in fade-in-0'>
                <p className='mb-1.5 text-sm font-medium text-muted-foreground'>Select Map Boundary Resolution:</p>
                {geographyOptions.map((opt) => {
                  const isSelected = selection.geographyLevel === opt.value;
                  return (
                    <GeographyButton
                      key={opt.value}
                      option={opt}
                      isSelected={isSelected}
                      onSelect={handleGeographySelectHandlers[opt.value]}
                    />
                  );
                })}
              </div>
            )}

            {/* Tab 3: Quality-of-Life Composite Index Builder */}
            {activeTab === "index" && (
              <div id='tour-composite-pane' className='flex flex-col min-h-0 flex-1 gap-3 animate-in fade-in-0'>
                {/* Presets Header */}
                <div className='shrink-0 flex items-center justify-between'>
                  <span className='flex items-center gap-1.5 text-xs font-bold text-foreground uppercase tracking-wider'>
                    <Sparkles className='size-3.5 text-primary' /> Index Presets
                  </span>
                  {activeMetricEntries.length > 0 && (
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={onResetComposite}
                      className='h-7 px-2 text-xs font-semibold text-muted-foreground hover:text-destructive'>
                      <RotateCcw className='mr-1 size-3' /> Reset
                    </Button>
                  )}
                </div>

                <div className='shrink-0 grid grid-cols-2 gap-2'>
                  {COMPOSITE_PRESETS.map((preset) => {
                    const isSelected = JSON.stringify(compositeWeights) === JSON.stringify(preset.weights);

                    return (
                      <Button
                        key={preset.id}
                        variant={isSelected ? "default" : "outline"}
                        size='sm'
                        onClick={() => {
                          handleApplyPreset(preset);
                        }}
                        className='h-auto justify-start py-2 px-2.5 text-left text-xs leading-snug font-medium'>
                        <div className='flex flex-col gap-0.5 w-full'>
                          <div className='flex items-center justify-between font-semibold'>
                            <span>{preset.name}</span>
                            {isSelected && <Check className='ml-1 size-3 shrink-0 text-primary-foreground' />}
                          </div>
                        </div>
                      </Button>
                    );
                  })}
                </div>

                {/* Active Weight Summary Counter */}
                <div className='shrink-0 flex items-center justify-between rounded-md border border-border bg-muted/40 px-3 py-2 text-xs'>
                  <span className='text-muted-foreground'>
                    Active:{" "}
                    <strong className='text-foreground'>
                      {activeMetricEntries.length} / {metricCatalogue.length}
                    </strong>
                  </span>
                  <span className='text-muted-foreground'>
                    Weight Sum: <strong className='text-foreground'>{totalWeight}%</strong>
                  </span>
                </div>

                {/* Dedicated Full-Height Scrollable Metric Weight Slider List */}
                <div
                  className={
                    variant === "sheet"
                      ? "space-y-2.5"
                      : "flex-1 min-h-0 space-y-2.5 overflow-y-auto pr-1 max-h-[calc(100vh-21rem)] lg:max-h-[calc(100vh-23rem)]"
                  }>
                  {metricCatalogue.map((metric: MetricDefinition) => {
                    const Icon = METRIC_ICONS[metric.id] || SlidersHorizontal;
                    const weight = compositeWeights[metric.id] ?? 0;
                    const direction = METRIC_DIRECTIONS[metric.id];

                    return (
                      <div
                        key={metric.id}
                        className={cn(
                          "rounded-md border p-2.5 transition-all",
                          weight > 0 ? "border-primary/50 bg-primary/5 shadow-xs" : "border-border bg-card",
                        )}>
                        <div className='flex items-center justify-between gap-2'>
                          <div className='flex items-center gap-2'>
                            <div
                              className={cn(
                                "flex size-7 shrink-0 items-center justify-center rounded-md text-xs font-bold",
                                weight > 0 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
                              )}>
                              <Icon className='size-3.5' />
                            </div>
                            <div>
                              <div className='text-xs font-bold text-foreground leading-tight'>{metric.label}</div>
                              <div className='text-xs text-muted-foreground'>
                                {direction === "lower-better" ? "Lower is better" : "Higher is better"}
                              </div>
                            </div>
                          </div>

                          <span
                            className={cn(
                              "rounded-xs px-1.5 py-0.5 text-xs font-extrabold font-mono",
                              weight > 0 ? "bg-primary/15 text-primary" : "bg-muted/80 text-muted-foreground",
                            )}>
                            {weight}%
                          </span>
                        </div>

                        <div className='mt-2 flex items-center gap-2'>
                          <input
                            type='range'
                            min={0}
                            max={100}
                            step={5}
                            value={weight}
                            onChange={(e) => {
                              handleSliderChange(metric.id, Number(e.target.value));
                            }}
                            className='h-2 w-full cursor-pointer appearance-none rounded-lg bg-muted accent-primary'
                            aria-label={`Weight slider for ${metric.label}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </CollapsibleRegion>
      </div>
    );
  },
);

ExplorerControls.displayName = "ExplorerControls";
